import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { PageHeader, SectionCard, Spinner } from '../components/ui';
import { fmtDate, timeAgo } from '../lib/format';

interface Grant {
  id: string;
  grantedBy: string;
  targetUserId: string;
  roomId: string | null;
  reason: string | null;
  grantedAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
}

export function PeekPage() {
  const [items, setItems] = useState<Grant[] | null>(null);
  const [target, setTarget] = useState('');
  const [roomId, setRoomId] = useState('');
  const [reason, setReason] = useState('');
  const [ttl, setTtl] = useState(60);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get<{ items: Grant[] }>('/api/crm/peek/grants').then((r) => setItems(r.items));

  useEffect(() => { load(); }, []);

  const grant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setBusy(true);
    try {
      await api.post('/api/crm/peek/grants', {
        targetUserId: target,
        roomId: roomId || undefined,
        reason: reason || undefined,
        ttlMinutes: ttl
      });
      setTarget(''); setRoomId(''); setReason('');
      load();
    } finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    try { await api.del(`/api/crm/peek/grants/${id}`); load(); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Пик" subtitle="Гранты на просмотр закрытых рук игроков" />

      <SectionCard title="Выдать грант" className="mb-4">
        <form onSubmit={grant} className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input className="input md:col-span-2" placeholder="userId игрока" value={target} onChange={(e) => setTarget(e.target.value)} required />
          <input className="input" placeholder="roomId (опц.)" value={roomId} onChange={(e) => setRoomId(e.target.value)} />
          <input className="input" type="number" min={1} max={1440} value={ttl} onChange={(e) => setTtl(Number(e.target.value))} title="TTL в минутах" />
          <button className="btn btn-gold" disabled={busy}><Icon.Plus size={14} />Выдать</button>
          <input className="input md:col-span-5" placeholder="Причина (опц.)" value={reason} onChange={(e) => setReason(e.target.value)} />
        </form>
      </SectionCard>

      <SectionCard title="Активные и история">
        {!items ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-4"><Spinner /> Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-stone-500 py-4">Пока нет грантов</div>
        ) : (
          <div className="overflow-x-auto -mx-3">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Комната</th>
                  <th>Причина</th>
                  <th>Выдан</th>
                  <th>Истекает</th>
                  <th>Статус</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((g) => {
                  const expired = g.expiresAt != null && g.expiresAt < Date.now();
                  const active = !g.revokedAt && !expired;
                  return (
                    <tr key={g.id}>
                      <td className="font-mono text-xs">{g.targetUserId.slice(0, 8)}…</td>
                      <td className="font-mono text-xs">{g.roomId ? g.roomId.slice(0, 8) + '…' : <span className="text-stone-500">любая</span>}</td>
                      <td className="text-sm">{g.reason ?? <span className="text-stone-500">—</span>}</td>
                      <td className="text-xs text-stone-400">{timeAgo(g.grantedAt)}</td>
                      <td className="text-xs text-stone-400">{g.expiresAt ? fmtDate(g.expiresAt) : <span className="text-stone-500">∞</span>}</td>
                      <td>
                        {g.revokedAt ? <span className="pill pill-red">отозван</span>
                          : expired ? <span className="pill pill-stone">истёк</span>
                          : <span className="pill pill-green">активен</span>}
                      </td>
                      <td className="text-right">
                        {active && (
                          <button className="btn btn-secondary !px-2 !py-1.5" disabled={busy} onClick={() => revoke(g.id)}>
                            <Icon.Trash size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
